import { type JSONContent } from "@tiptap/core";
import { DOMParser, Fragment, Node, type Schema } from "@tiptap/pm/model";

/**
 * Converts a string containing HTML to a ProseMirror Fragment or Node.
 *
 * @param {string} htmlString - The input HTML string to convert.
 * @param {Schema} schema - The ProseMirror schema to use for parsing.
 * @returns {Fragment | Node} A ProseMirror Fragment (if successful) or ProseMirror Node (if error) representing the parsed content.
 */
export function convertStringToFragment(htmlString: string, schema: Schema): Fragment | Node {
  try {
    // Sanitize the HTML string (e.g., remove script tags)
    const sanitizedHtml = sanitizeHtmlString(htmlString);

    const tempElement = document.createElement("div");
    tempElement.innerHTML = sanitizedHtml;

    const domParser = DOMParser.fromSchema(schema);
    const parsedNode = domParser.parse(tempElement); // This parses the HTML into a ProseMirror doc node

    // Ensure we return the content fragment, not the top-level doc node itself
    return parsedNode.content;
  } catch (error) {
    console.error("Error converting HTML string to Fragment:", error);
    // Fallback: Return a paragraph node with the original (sanitized) HTML string as text
    const sanitizedText = sanitizeHtmlString(htmlString); // Sanitize again just in case error was during parsing
    const content = schema.text(sanitizedText);
    return schema.nodes.paragraph.create(null, content);
  }
}

/**
 * Converts a string containing HTML to Tiptap's JSONContent format.
 *
 * @param {string} htmlString - The input HTML string to convert.
 * @param {Schema} schema - The ProseMirror schema to use for parsing.
 * @returns {JSONContent | null} A JSONContent object representing the parsed content, or null if conversion fails.
 */
export function convertStringToJSONContent(htmlString: string, schema: Schema): JSONContent | null {
  try {
    const fragmentOrNode = convertStringToFragment(htmlString, schema);

    let docNode: Node;
    // Check if the result is a Fragment before calling toJSON
    if (fragmentOrNode instanceof Fragment) {
      // This check should now work
      // Wrap the fragment in a doc node before converting to JSON
      docNode = schema.node("doc", null, fragmentOrNode);
    } else if (fragmentOrNode instanceof Node && fragmentOrNode.type.name === "paragraph") {
      // Use Node as value
      // If conversion failed and returned a single paragraph node, wrap it in a doc
      docNode = schema.node("doc", null, [fragmentOrNode]);
    } else {
      console.error(
        "[convertStringToJSONContent] convertStringToFragment did not return a Fragment or a fallback Paragraph node. Result:",
        fragmentOrNode,
      );
      // Fallback: create a doc with a single paragraph containing the original sanitized text
      const sanitizedText = sanitizeHtmlString(htmlString);
      const content = schema.text(sanitizedText);
      const paragraphNode = schema.nodes.paragraph.create(null, content);
      docNode = schema.node("doc", null, [paragraphNode]);
    }

    const jsonResult = docNode.toJSON();
    return jsonResult;
  } catch (error) {
    console.error("Error converting HTML string to JSONContent:", error);
    return null; // Return null on error
  }
}

/**
 * Sanitizes an HTML string (currently just removes script tags).
 *
 * @param htmlString - The HTML string to sanitize.
 * @returns The sanitized HTML string.
 */
export function sanitizeHtmlString(htmlString: string): string {
  try {
    // Remove any script tags that may be present in the HTML
    // Consider adding more sanitization rules if needed
    return htmlString.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  } catch (error) {
    console.error("Error sanitizing HTML string:", error);
    // Return original string on error, or consider a safer fallback
    return htmlString;
  }
}
